import type {
  ApiInfrastructureErrors,
  BackendApiErrorUnion,
} from '../../../data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { liftRequest } from '../context';
import { BackendApi } from '../../../service/api/BackendApi';
import type { AuthToken } from '../../../service/auth/AuthToken';
import { ApiModule } from '../../../service/logger/ApiModule';
import type { LoggerConfig } from '../../../service/logger/LoggerConfig';
import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import type { ProductsOrderByT } from '@vs-shop/schemas/enum/productsOrderBy';
import type { ProductListingContextId } from '@vs-shop/schemas/id/ProductsContextId';
import {
  HydraCollectionResponse,
  type HydraCollectionResponseType,
} from '@vs-shop/schemas/openapi/response';
import type { AllFiltersState } from '@vs-shop/schemas/product/filter/state/all';
import { combineFiltersToApiQuery } from '@vs-shop/schemas/product/filter/stateToApi/combineFiltersToApiQuery';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { DateTime, Schema as SchemaType } from 'effect';
import { Effect, Match, Request, RequestResolver } from 'effect';
import type * as HttpClient from 'effect/unstable/http/HttpClient';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

import { getContextOrderParams, HTTP_METHOD, PRODUCTS_ENDPOINT } from './_shared';

export type MakeGetProductsByContextIdDeps<ProductsByContextId extends SchemaType.Top> = {
  readonly schemas: {
    readonly productsByContextId: ProductsByContextId;
  };
};

type GetProductsByContextIdProps = {
  id: ProductListingContextId;
  page: number;
  itemsPerPage: number;
  filters: AllFiltersState;
  orderBy: ProductsOrderByT;
};

export const makeGetProductsByContextId = <ProductsByContextId extends SchemaType.Top>(
  deps: MakeGetProductsByContextIdDeps<ProductsByContextId>
) => {
  const GetProductsByContextIdError = createBackendApiError({
    domain: 'Product',
    endpoint: 'GetProductsByContextId',
    path: PRODUCTS_ENDPOINT,
    identifierField: 'id',
  });

  type ProductsByContextIdRequestError = BackendApiErrorUnion<typeof GetProductsByContextIdError>;

  const getProductsByContextIdFetch = ({
    id,
    page,
    itemsPerPage,
    filters,
    orderBy,
  }: GetProductsByContextIdProps) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      const filterByContext = Match.value(id).pipe(
        Match.discriminator('kind')('taxon', (context) => ({ taxon: context.value })),
        Match.discriminator('kind')('brand', (context) => ({ brand: [context.value] })),
        Match.exhaustive
      );
      const filtersPrepared = combineFiltersToApiQuery(filters);
      const orderParams = getContextOrderParams(orderBy);

      return yield* apiClient
        .path(PRODUCTS_ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({
          query: {
            ...filterByContext,
            page,
            inStock: true,
            itemsPerPage,
            ...filtersPrepared,
            ...orderParams,
          },
        });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${PRODUCTS_ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'products.contextType': id.kind,
          'products.contextId': id.value,
          'products.page': page,
          'products.itemsPerPage': itemsPerPage,
          'products.orderBy': orderBy,
        },
      })
    );

  const getProductsByContextId = (props: GetProductsByContextIdProps) =>
    getProductsByContextIdFetch(props).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) =>
            schemaBodyJsonClean(HydraCollectionResponse(deps.schemas.productsByContextId))(
              response
            ),
          404: (response) =>
            Effect.fail(new GetProductsByContextIdError.NotFoundError({ id: props.id, response })),
          orElse: (response) =>
            Effect.fail(
              new GetProductsByContextIdError.ApiRequestError({ id: props.id, response })
            ),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.ProductList,
          'Debug',
          `Resolved ${String(result['hydra:member'].length)}/${String(result['hydra:totalItems'])} products for ${props.id.kind}:${props.id.value}, page ${String(props.page)}, itemsPerPage ${String(props.itemsPerPage)}`
        )
      ),
      Effect.withSpan('api.product.list', {
        kind: 'internal',
        attributes: {
          'products.contextType': props.id.kind,
          'products.contextId': props.id.value,
          'products.page': props.page,
          'products.itemsPerPage': props.itemsPerPage,
          'products.orderBy': props.orderBy,
        },
      })
    );

  type GetProductsByContextIdRequest = {
    readonly _tag: 'GetProductsByContextIdRequest';
    readonly id: ProductListingContextId;
    readonly page: number;
    readonly itemsPerPage: number;
    readonly filters: AllFiltersState;
    readonly orderBy: ProductsOrderByT;
  } & Request.Request<
    HydraCollectionResponseType<typeof deps.schemas.productsByContextId>,
    ProductsByContextIdRequestError | ApiInfrastructureErrors,
    BackendApi | AuthToken | HttpClient.HttpClient | LoggerConfig | DateTime.CurrentTimeZone
  >;

  const GetProductsByContextIdRequest = Request.tagged<GetProductsByContextIdRequest>(
    'GetProductsByContextIdRequest'
  );

  const GetProductsByContextIdResolver = RequestResolver.fromEffect(
    liftRequest((request: GetProductsByContextIdRequest) =>
      getProductsByContextId({
        id: request.id,
        page: request.page,
        itemsPerPage: request.itemsPerPage,
        filters: request.filters,
        orderBy: request.orderBy,
      })
    )
  );

  const requestProductsByContextId = (props: GetProductsByContextIdProps) =>
    Effect.request(GetProductsByContextIdRequest(props), GetProductsByContextIdResolver);

  return { requestProductsByContextId, GetProductsByContextIdError };
};
